import { Box, Typography } from "@mui/material";
import {
  AUTH_CARD_BORDER,
  AUTH_TEXT_SECONDARY,
  BRAND_BLUE,
  BRAND_BLUE_DARK,
  BRAND_BLUE_LIGHT,
} from "../theme/appTheme";
import { useTranslation } from "../i18n/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

// Shared shell for every auth "form" screen — Login, Register, Forgot
// Password, and the code-entry step: a branded gradient panel on the left,
// the form card on the right.
function AuthLayout({ children, cardWidth = 420 }) {
  const year = new Date().getFullYear();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: "100vh", display: "flex" }}>
      <LanguageSwitcher />
      {/* Left — brand panel */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: "0 0 42%",
          position: "relative",
          flexDirection: "column",
          justifyContent: "space-between",
          overflow: "hidden",
          p: { md: 5, lg: 6 },
          background: `linear-gradient(155deg, ${BRAND_BLUE_DARK} 0%, ${BRAND_BLUE} 55%, ${BRAND_BLUE_LIGHT} 120%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-12%",
            right: "-8%",
            width: 340,
            height: 340,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.14), transparent 70%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-18%",
            left: "-12%",
            width: 420,
            height: 420,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.10), transparent 70%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "12%",
            right: "8%",
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <img
            src="/Sibolab_Logo_white_rgb.png"
            alt="Sibolab"
            style={{ height: "46px", width: "auto", objectFit: "contain", display: "block" }}
          />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 420 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { md: "2rem", lg: "2.25rem" },
              color: "#ffffff",
              lineHeight: 1.2,
              mb: 1.5,
            }}
          >
            {t("auth.layout.title")}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.82)",
              fontSize: "0.9375rem",
              lineHeight: 1.6,
            }}
          >
            {t("auth.layout.subtitleDesktop")}
          </Typography>
        </Box>

        <Typography sx={{ position: "relative", zIndex: 1, color: "rgba(255,255,255,0.55)", fontSize: "0.75rem" }}>
          {t("auth.layout.copyright", { year })}
        </Typography>
      </Box>

      {/* Right — form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
          p: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: cardWidth }}>
          <Box sx={{ display: { xs: "block", md: "none" }, textAlign: "center", mt: -4, mb: 3.5 }}>
            <img
              src="/Sibolab_Logo_darkblue_rgb.png"
              alt="Sibolab"
              style={{ height: "44px", width: "auto", objectFit: "contain", display: "inline-block", marginBottom: 24 }}
            />
            <Typography sx={{ fontWeight: 700, fontSize: "1.375rem", color: "#163666" }}>
              {t("auth.layout.title")}
            </Typography>
            <Typography sx={{ color: AUTH_TEXT_SECONDARY, fontSize: "0.8125rem", mt: 0.5 }}>
              {t("auth.layout.subtitleMobile")}
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: "#ffffff",
              border: `1px solid ${AUTH_CARD_BORDER}`,
              borderRadius: 3,
              p: { xs: 3, sm: 4 },
              boxShadow: "0 12px 32px rgba(15,23,42,0.06), 0 2px 8px rgba(15,23,42,0.04)",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AuthLayout;
